import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    InputAdornment,
    Option,
    SelectField,
    TextField,
    Typography,
} from '@deere/fuel-react';
import { DateTime } from 'luxon';
import { PropTypes } from 'prop-types';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AdminContext } from '../context/AdminContext';
import styles from './BidForm.module.css';
import { DateInput } from './DateInput';
import { PromptValidState } from './navigation/PromptValidState';

const MAX_ADDITIONAL_DETAILS_LENGTH = 5000;

// Normalizes \r\n/\r to \n so our char count matches what the browser's native textarea counts.
const normalizeLineEndings = (text) => text.replace(/\r\n|\r/g, '\n');

const requiredFieldsMap = {
    //Davenport
    3: [
        'facility',
        'department',
        'biddingGroup',
        'postingStartDate',
        'classification',
        'jobGrade',
        'cipp',
        'noOfOpenings',
    ],
    //NAPDC
    6: [
        'facility',
        'department',
        'postingStartDate',
        'classification',
        'jobGrade',
        'cipp',
        'noOfOpenings',
        'seniorityForJobBid',
        'biddingGroup',
    ],
    //Horicon
    15: [
        'facility',
        'department',
        'currentWorkforce',
        'postingStartDate',
        'classification',
        'jobGrade',
        'noOfOpenings',
        'seniorityForJobBid',
        'biddingGroup',
        'seniority1',
        'seniority2',
        'seniority3',
        'seniority4',
    ],
    //Dubuque
    16: [
        'facility',
        'department',
        'postingStartDate',
        'classification',
        'jobGrade',
        'cipp',
        'noOfOpenings',
        'biddingGroup',
    ],
    //Springfield
    19: [
        'facility',
        'department',
        'postingStartDate',
        'classification',
        'jobGrade',
        'noOfOpenings',
        'seniorityForJobBid',
        'biddingGroup',
        'seniority1',
        'seniority2',
        'seniority3',
        'seniority4',
    ],
    //Coffeyville
    17: [
        'facility',
        'department',
        'postingStartDate',
        'classification',
        'jobGrade',
        'noOfOpenings',
        'biddingGroup',
    ],
    //Paton
    20: [
        'facility',
        'department',
        'postingStartDate',
        'classification',
        'jobGrade',
        'noOfOpenings',
        'seniorityForJobBid',
        'biddingGroup',
        'shifts',
    ],
    //Ottumwa
    9: [
        'facility',
        'department',
        'postingStartDate',
        'classification',
        'jobGrade',
        'cipp',
        'noOfOpenings',
        'seniorityForJobBid',
        'biddingGroup',
        'shifts',
    ],
    // Des moines IAM
    4: [
        'facility',
        'department',
        'currentWorkforce',
        'postingStartDate',
        'classification',
        'jobGrade',
        'cipp',
        'cippPerc',
        'noOfOpenings',
        'seniorityForJobBid',
        'biddingGroup',
        'seniority1',
        'seniority2',
        'seniority3',
        'seniority4',
    ],
    // Des moines UAW
    5: [
        'facility',
        'department',
        'currentWorkforce',
        'postingStartDate',
        'classification',
        'jobGrade',
        'cipp',
        'cippPerc',
        'noOfOpenings',
        'seniorityForJobBid',
        'biddingGroup',
        'seniority1',
        'seniority2',
        'seniority3',
        'seniority4',
    ],
    //Augusta
    14: [
        'facility',
        'department',
        'postingStartDate',
        'classification',
        'jobGrade',
        'noOfOpenings',
        'seniorityForJobBid',
        'biddingGroup',
        'shifts',
    ],
    //Harvester UAW
    10: [
        'facility',
        'department',
        'postingStartDate',
        'classification',
        'jobGrade',
        'cipp',
        'noOfOpenings',
        'seniorityForJobBid',
        'biddingGroup',
    ],
    //Harvester IAM
    11: [
        'facility',
        'department',
        'postingStartDate',
        'classification',
        'jobGrade',
        'cipp',
        'noOfOpenings',
        'seniorityForJobBid',
        'biddingGroup',
    ],
    default: [
        'facility',
        'department',
        'currentWorkforce',
        'postingStartDate',
        'classification',
        'jobGrade',
        'otPerc',
        'cipp',
        'cippPerc',
        'noOfOpenings',
        'seniorityForJobBid',
        'biddingGroup',
        'shifts',
        'seniority1',
        'seniority2',
        'seniority3',
        'seniority4',
    ],
    // Add more mappings as needed
};

export const BidForm = (props) => {
    const { bidObj } = props;
    const { selectedAdminGroup, loading, error, setIsJobFormDirty } =
        useContext(AdminContext);
    const [requiredFields, setRequiredFields] = useState([]);
    const clearForm = useCallback(() => {
        setFacility(null);
        setDepartment('');
        setCurrentWorkforce('');
        setBiddingGroup(null);
        setPostingStartDate(new Date());
        setEndDate(new Date());
        setClassification('');
        setJobGrade('');
        setSupervisors('');
        setOtPerc('');
        setCipp('');
        setCippPerc('');
        setNoOfOpenings('');
        setShift1(false);
        setShift2(false);
        setShift3(false);
        setShift4(false);
        setSeniorityForJobBid('');
        setAdditionalDetails('');
        setSeniority1(new Date());
        setSeniority2(new Date());
        setSeniority3(new Date());
        setSeniority4(new Date());
        setQualifications('');
        setGreenLine('');
        setReqNumber('');
    }, []);

    // --- FIELDS ---
    // Facility
    const [facility, setFacility] = useState(bidObj.businessUnit ?? null);
    const [facilityHasChanged, setFacilityHasChanged] = useState(false);
    const isFacilityValid = (value) => {
        return value !== null && value !== undefined;
    };
    const [facilityIsValid, setFacilityIsValid] = useState(
        isFacilityValid(bidObj.businessUnit ?? null)
    );
    const facilityChangeHandler = (event) => {
        const businessUnitId = event.target.value;
        const updatedBusinessUnit =
            selectedAdminGroup.facilityOrBusinessUnits.find(
                (bu) => bu.id === parseInt(businessUnitId)
            ) ?? null;

        setFacilityHasChanged(true);
        setIsFormDirty(true);
        setFacility(updatedBusinessUnit);
        setFacilityIsValid(isFacilityValid(updatedBusinessUnit));
    };

    // Department
    const [department, setDepartment] = useState(bidObj.department ?? '');
    const [departmentHasChanged, setDepartmentHasChanged] = useState(false);
    const isDepartmentValid = (value) => {
        return value !== '';
    };
    const [departmentIsValid, setDepartmentIsValid] = useState(
        isDepartmentValid(bidObj.department ?? '')
    );
    const departmentChangeHandler = (event) => {
        setDepartmentHasChanged(true);
        setIsFormDirty(true);
        const value = event.target.value;
        setDepartment(value);
        setDepartmentIsValid(isDepartmentValid(value));
    };

    // Current Workforce
    const [currentWorkforce, setCurrentWorkforce] = useState(
        bidObj.currentWorkforce ?? ''
    );
    const [currentWorkforceHasChanged, setCurrentWorkforceHasChanged] =
        useState(false);
    const isCurrentWorkforceValid = (value) => {
        const validWithoutCommas = /^[1-9]{1}[0-9]*$/.test(value);
        const validWithPossibleCommas = /^[1-9]{1}[0-9]{1,2}(,[0-9]{3})*$/.test(
            value
        );
        return validWithoutCommas || validWithPossibleCommas;
        // return /^[0-9]+(,[0-9]+)*$/.test(value)
    };
    const [currentWorkforceIsValid, setCurrentWorkforceIsValid] = useState(
        isCurrentWorkforceValid(bidObj.currentWorkforce ?? '')
    );
    const currentWorkforceHandler = (event) => {
        setCurrentWorkforceHasChanged(true);
        setIsFormDirty(true);
        setCurrentWorkforce(event.target.value);
        setCurrentWorkforceIsValid(isCurrentWorkforceValid(event.target.value));
    };
    //active job details page call status
    const [fromActiveJobAndUnchanged, setFromActiveJobAndUnchanged] = useState(
        props.activeCopyCallCr ?? false
    );

    // Bidding Group For This Job Bid
    const initializeBiddingGroup = useCallback(() => {
        if (loading || !selectedAdminGroup?.biddingGroups) {
            return null;
        }
        if (bidObj?.biddingGroup) {
            return (
                selectedAdminGroup?.biddingGroups.find(
                    (bg) => bg.id === bidObj.biddingGroup.id
                ) ?? ''
            );
        }
        const validBiddingGroups = selectedAdminGroup?.biddingGroups.filter(
            (bg) => bg.hasCycleSettings
        );
        if (validBiddingGroups.length === 1) {
            return validBiddingGroups[0];
        }
        return null;
    }, [bidObj, loading, selectedAdminGroup]);
    const [biddingGroup, setBiddingGroup] = useState(initializeBiddingGroup);

    const isBiddingGroupValid = (value) => {
        return value !== null && value !== undefined;
    };

    useEffect(() => {
        // resets bidding group on context change
        const initialBiddingGroup = initializeBiddingGroup();
        setBiddingGroup(initialBiddingGroup);
        setBiddingGroupIsValid(isBiddingGroupValid(initialBiddingGroup));
        setRequiredFields(
            requiredFieldsMap[initialBiddingGroup?.id] ||
                requiredFieldsMap.default
        );
    }, [initializeBiddingGroup]);

    const [biddingGroupHasChanged, setBiddingGroupHasChanged] = useState(false);
    const [biddingGroupIsValid, setBiddingGroupIsValid] = useState(
        isBiddingGroupValid(initializeBiddingGroup())
    );

    const biddingGroupHandler = (event) => {
        const biddingGroupId = event.target.value;
        const updatedBiddingGroup =
            selectedAdminGroup.biddingGroups.find(
                (bg) => bg.id === parseInt(biddingGroupId)
            ) ?? null;
        setBiddingGroupHasChanged(true);
        setIsFormDirty(true);
        setBiddingGroup(updatedBiddingGroup);
        setBiddingGroupIsValid(isBiddingGroupValid(updatedBiddingGroup));
        setRequiredFields(
            requiredFieldsMap[updatedBiddingGroup?.id] ||
                requiredFieldsMap.default
        );
    };

    // Job Bid Posting Start Date
    const [postingStartDate, setPostingStartDate] = useState(
        bidObj.postingStartDate
            ? DateTime.fromJSDate(
                  new Date(bidObj.postingStartDate)
              ).toLocaleString(DateTime.DATETIME_MED)
            : ' '
    );

    const isPostingStartDateValid = (value) => {
        // TODO: are we on the correct day
        // TODO: and the correct time?

        return new Date(value).getTime() >= Date.now();
    };
    const [postingStartDateIsValid, setPostingStartDateIsValid] = useState(
        isPostingStartDateValid(
            bidObj.postingStartDate ? new Date(bidObj.postingStartDate) : ' '
        )
    );

    const postingDateHandler = (date) => {
        setFromActiveJobAndUnchanged(props.activeCopyCallCr ?? false);
        setIsFormDirty(true);
        //TODO: split date from time?
        const timeToGoActive = DateTime.fromISO(biddingGroup.timeToGoActive);
        const calculatedStartDate = DateTime.fromJSDate(new Date(date)).set({
            hour: timeToGoActive.hour,
            minute: timeToGoActive.minute,
        });
        setPostingStartDate(
            calculatedStartDate.toLocaleString(DateTime.DATETIME_MED)
        );

        setPostingStartDateIsValid(
            isPostingStartDateValid(calculatedStartDate)
        );

        const timeToClose = DateTime.fromISO(biddingGroup.timeToClose);
        const calculatedEndDate = DateTime.fromJSDate(new Date(date))
            .plus({ days: biddingGroup.daysInActivePeriod - 1 })
            .set({ hour: timeToClose.hour, minute: timeToClose.minute });
        setEndDate(calculatedEndDate.toLocaleString(DateTime.DATETIME_MED));
    };

    // Job Bid End Date
    const [endDate, setEndDate] = useState(
        bidObj.endDate
            ? DateTime.fromJSDate(new Date(bidObj.endDate)).toLocaleString(
                  DateTime.DATETIME_MED
              )
            : ' '
    );

    const [classification, setClassification] = useState(
        bidObj.classification ?? ''
    );
    const [classificationHasChanged, setClassificationHasChanged] =
        useState(false);
    const isClassificationValid = (value) => {
        return value !== '';
    };
    const [classificationIsValid, setClassificationIsValid] = useState(
        isClassificationValid(bidObj.classification ?? '')
    );
    const classificationChangeHandler = (event) => {
        const classificationId = event.target.value;
        const updatedClassification =
            props.classificationList.find(
                (cl) => cl.id === parseInt(classificationId)
            ) ?? null;
        //also set job grade...
        if (updatedClassification !== null) {
            jobGradeUiSetter(updatedClassification.jobGradeValue ?? '');
        }
        setClassificationHasChanged(true);
        setIsFormDirty(true);
        setClassification(updatedClassification);
        setClassificationIsValid(isClassificationValid(event.target.value));
    };

    // Job Grade
    const [jobGrade, setJobGrade] = useState(bidObj.jobGrade ?? '');
    const [jobGradeHasChanged, setJobGradeHasChanged] = useState(false);
    const isJobGradeValid = (value) => {
        return value !== '';
    };
    const [JobGradeIsValid, setJobGradeIsValid] = useState(
        isJobGradeValid(bidObj.jobGrade ?? '')
    );
    const jobGradeChangeHandler = (event) => {
        jobGradeUiSetter(event.target.value);
    };
    const jobGradeUiSetter = (value) => {
        setJobGradeHasChanged(true);
        setIsFormDirty(true);
        setJobGrade(value);
        setJobGradeIsValid(isJobGradeValid(value));
    };

    // Supervisors (optional)
    const [supervisors, setSupervisors] = useState(bidObj.supervisors ?? '');
    const supervisorsChangeHandler = (event) => {
        setIsFormDirty(true);
        setSupervisors(event.target.value);
    };

    // OT%
    const [otPerc, setOtPerc] = useState(bidObj.otPerc ?? '');
    const [otPercHasChanged, setOtPercHasChanged] = useState(false);
    const isOtPercValid = (value) => {
        const amount = parseInt(value);
        return !isNaN(amount) && amount <= 100 && amount >= 0;
    };
    const [otPercIsValid, setOtPercIsValid] = useState(
        isOtPercValid(bidObj.otPerc ?? '')
    );
    const otPercChangeHandler = (event) => {
        setOtPercHasChanged(true);
        setIsFormDirty(true);
        setOtPerc(event.target.value);
        setOtPercIsValid(isOtPercValid(event.target.value));
    };

    // CIP
    const [cipp, setCipp] = useState(
        bidObj.cipp === false ? 'N' : bidObj.cipp === true ? 'Y' : ''
    );
    const [cippHasChanged, setCippHasChanged] = useState(false);
    const isCippValid = (value) => {
        return value !== '';
    };
    const [cippIsValid, setCippIsValid] = useState(
        isCippValid(
            bidObj.cipp === false ? 'N' : bidObj.cipp === true ? 'Y' : ''
        )
    );
    const cippChangeHandler = (event) => {
        setCippHasChanged(true);
        setIsFormDirty(true);
        setCipp(event.target.value);
        setCippIsValid(isCippValid(event.target.value));
        if (event.target.value === 'N') {
            setCippPerc('0');
            setCippPercIsValid(true);
        } else {
            setCippPerc('');
            setCippPercIsValid(false);
        }
    };

    // CIP%
    const [cippPerc, setCippPerc] = useState(bidObj.cippPerc ?? '');
    const [cippPercHasChanged, setCippPercHasChanged] = useState(false);
    const isCippPercValid = (value) => {
        const isValidFormat = /\d{1,3}%?/.test(value);
        const amount = parseInt(value);
        const isValidValue = !isNaN(amount) && amount <= 500 && amount >= 0;
        if (isValidFormat && isValidValue) {
            return true;
        }
        return false;
    };
    const [cippPercIsValid, setCippPercIsValid] = useState(
        isCippPercValid(bidObj.cippPerc ?? '')
    );
    const cippPercHandler = (event) => {
        setCippPercHasChanged(true);
        setIsFormDirty(true);
        setCippPerc(event.target.value);
        setCippPercIsValid(isCippPercValid(event.target.value));
    };

    // Number of Openings
    const [noOfOpenings, setNoOfOpenings] = useState(bidObj.noOfOpenings ?? '');
    const [noOfOpeningsHasChanged, setNoOfOpeningsHasChanged] = useState(false);
    const isNoOfOpeningsValid = (value) => {
        const amount = parseInt(value);
        return !isNaN(amount) && amount <= 999 && amount >= 0;
    };
    const [noOfOpeningsIsValid, setNoOfOpeningsIsValid] = useState(
        isNoOfOpeningsValid(bidObj.noOfOpenings ?? '')
    );
    const noOfOpeningsHandler = (event) => {
        setFromActiveJobAndUnchanged(props.activeCopyCallCr ?? false);
        setNoOfOpeningsHasChanged(true);
        setIsFormDirty(true);
        setNoOfOpenings(event.target.value);
        setNoOfOpeningsIsValid(isNoOfOpeningsValid(event.target.value));
    };

    // Shifts Department Runs
    const [shift1, setShift1] = useState(bidObj.shift1 ?? false);
    const [shift2, setShift2] = useState(bidObj.shift2 ?? false);
    const [shift3, setShift3] = useState(bidObj.shift3 ?? false);
    const [shift4, setShift4] = useState(bidObj.shift4 ?? false);

    const [shiftHasChanged, setShiftHasChanged] = useState(false);

    const areShiftsValid = (
        shift1Input,
        shift2Input,
        shift3Input,
        shift4Input
    ) => {
        return shift1Input || shift2Input || shift3Input || shift4Input;
    };
    const [shiftsAreValid, setShiftsAreValid] = useState(
        areShiftsValid(
            bidObj.shift1 ?? false,
            bidObj.shift2 ?? false,
            bidObj.shift3 ?? false,
            bidObj.shift4 ?? false
        )
    );

    const shift1Handler = (event) => {
        setShiftHasChanged(true);
        setIsFormDirty(true);
        setShift1(event.target.checked);
        setShiftsAreValid(
            areShiftsValid(event.target.checked, shift2, shift3, shift4)
        );
    };
    const shift2Handler = (event) => {
        setShiftHasChanged(true);
        setIsFormDirty(true);
        setShift2(event.target.checked);
        setShiftsAreValid(
            areShiftsValid(shift1, event.target.checked, shift3, shift4)
        );
    };
    const shift3Handler = (event) => {
        setShiftHasChanged(true);
        setIsFormDirty(true);
        setShift3(event.target.checked);
        setShiftsAreValid(
            areShiftsValid(shift1, shift2, event.target.checked, shift4)
        );
    };
    const shift4Handler = (event) => {
        setShiftHasChanged(true);
        setIsFormDirty(true);
        setShift4(event.target.checked);
        setShiftsAreValid(
            areShiftsValid(shift1, shift2, shift3, event.target.checked)
        );
    };

    // Seniority For This Job
    const [seniorityForJobBid, setSeniorityForJobBid] = useState(
        bidObj.seniorityForJobBid ?? ''
    );
    const [seniorityHasChanged, setSeniorityHasChanged] = useState(false);
    const isSeniorityForJobBidValid = (value) => {
        return value !== '';
    };
    const [seniorityForJobBidIsValid, setSeniorityForJobBidIsValid] = useState(
        isSeniorityForJobBidValid(bidObj.seniorityForJobBid ?? '')
    );
    const seniorityForJobBidHandler = (event) => {
        setSeniorityHasChanged(true);
        setIsFormDirty(true);
        setSeniorityForJobBid(event.target.value);
        setSeniorityForJobBidIsValid(
            isSeniorityForJobBidValid(event.target.value)
        );
    };

    // Additional Details (optional)
    const [additionalDetails, setAdditionalDetails] = useState(
        normalizeLineEndings(bidObj.additionalDetails ?? '').slice(
            0,
            MAX_ADDITIONAL_DETAILS_LENGTH
        )
    );
    const additionalDetailsHandler = (event) => {
        setIsFormDirty(true);
        setAdditionalDetails(
            normalizeLineEndings(event.target.value).slice(
                0,
                MAX_ADDITIONAL_DETAILS_LENGTH
            )
        );
    };

    const getFormattedSeniority = (seniorityDateString) => {
        return seniorityDateString
            ? DateTime.fromISO(seniorityDateString).toJSDate()
            : new Date(); //.toFormat('yyyy-LL-dd') : new Date();
    };

    // Junior Permanent Seniority on 1st (conditionally optional)
    const [seniority1, setSeniority1] = useState(
        getFormattedSeniority(bidObj.seniority1)
    );
    const isSeniority1Valid = (value) => {
        if (!shift1) return true;
        // todo: is this supposed to be in the past? value.getTime() < Date.now()
        return (
            value !== null &&
            value !== '' &&
            new Date(value).getTime() < Date.now()
        );
    };
    const [seniority1IsValid, setSeniority1IsValid] = useState(
        isSeniority1Valid(getFormattedSeniority(bidObj.seniority1))
    );
    const seniority1Handler = (event) => {
        setIsFormDirty(true);
        setSeniority1(event);
        setSeniority1IsValid(isSeniority1Valid(event));
    };
    // Junior Permanent Seniority on 2nd (conditionally optional)
    const [seniority2, setSeniority2] = useState(
        getFormattedSeniority(bidObj.seniority2)
    );
    const isSeniority2Valid = (value) => {
        if (!shift2) return true;
        // todo: is this supposed to be in the past? value.getTime() < Date.now()
        return (
            value !== null &&
            value !== '' &&
            new Date(value).getTime() < Date.now()
        );
    };
    const [seniority2IsValid, setSeniority2IsValid] = useState(
        isSeniority2Valid(getFormattedSeniority(bidObj.seniority2))
    );
    const seniority2Handler = (event) => {
        setIsFormDirty(true);
        setSeniority2(event);
        setSeniority2IsValid(isSeniority2Valid(event));
    };
    // Junior Permanent Seniority on 3rd (conditionally optional)
    const [seniority3, setSeniority3] = useState(
        getFormattedSeniority(bidObj.seniority3)
    );
    const isSeniority3Valid = (value) => {
        if (!shift3) return true;
        // todo: is this supposed to be in the past? value.getTime() < Date.now()
        return (
            value !== null &&
            value !== '' &&
            new Date(value).getTime() < Date.now()
        );
    };
    const [seniority3IsValid, setSeniority3IsValid] = useState(
        isSeniority3Valid(getFormattedSeniority(bidObj.seniority3))
    );
    const seniority3Handler = (event) => {
        setIsFormDirty(true);
        setSeniority3(event);
        setSeniority3IsValid(isSeniority3Valid(event));
    };
    // Junior Permanent Seniority on 4th (conditionally optional)
    const [seniority4, setSeniority4] = useState(
        getFormattedSeniority(bidObj.seniority4)
    );
    const isSeniority4Valid = (value) => {
        if (!shift4) return true;
        // todo: is this supposed to be in the past? value.getTime() < Date.now()
        return (
            value !== null &&
            value !== '' &&
            new Date(value).getTime() < Date.now()
        );
    };
    const [seniority4IsValid, setSeniority4IsValid] = useState(
        isSeniority4Valid(getFormattedSeniority(bidObj.seniority4))
    );
    const seniority4Handler = (event) => {
        setIsFormDirty(true);
        setSeniority4(event);
        setSeniority4IsValid(isSeniority4Valid(event));
    };

    // Qualifications (optional)
    const [qualifications, setQualifications] = useState(
        bidObj.qualifications ?? ''
    );
    const qualificationsHandler = (event) => {
        setIsFormDirty(true);
        setQualifications(event.target.value);
    };

    // Green-Line (optional)
    const [greenLine, setGreenLine] = useState(bidObj.greenLine ?? '');
    const greenLineHandler = (event) => {
        setIsFormDirty(true);
        setGreenLine(event.target.value);
    };

    // Req Number (optional)
    const [reqNumber, setReqNumber] = useState(bidObj.reqNumber ?? '');
    const reqNumberChangeHandler = (event) => {
        setIsFormDirty(true);
        setReqNumber(event.target.value);
    };

    // --- END OF FIELDS ---
    const areAllFieldsValid = () => {
        const fieldValidity = {
            facility: facilityIsValid,
            department: departmentIsValid,
            currentWorkforce: currentWorkforceIsValid,
            postingStartDate: postingStartDateIsValid,
            biddingGroup: biddingGroupIsValid,
            classification: classificationIsValid,
            jobGrade: JobGradeIsValid,
            otPerc: otPercIsValid,
            cipp: cippIsValid,
            cippPerc: cippPercIsValid,
            noOfOpenings: noOfOpeningsIsValid,
            shifts: shiftsAreValid,
            seniorityForJobBid: seniorityForJobBidIsValid,
            seniority1: seniority1IsValid,
            seniority2: seniority2IsValid,
            seniority3: seniority3IsValid,
            seniority4: seniority4IsValid,
        };

        return requiredFields.every((field) => fieldValidity[field]);
    };
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [isFormDirty, setIsFormDirty] = useState(false);

    useEffect(() => {
        setIsJobFormDirty?.(isFormDirty);
        return () => setIsJobFormDirty?.(false);
    }, [isFormDirty, setIsJobFormDirty]);

    const saveHandler = () => {
        setSubmitAttempted(true);
        props.updateBid(createBidObj(), areAllFieldsValid());
        if (areAllFieldsValid()) {
            setIsFormDirty(false);
        }
    };
    const createBidObj = () => {
        const createdBidObject = {
            businessUnit: facility,
            department,
            classification,
            cipp: cipp === 'Y',
            currentWorkforce,
            noOfOpenings,
            supervisors,
            cippPerc,
            otPerc,
            seniority1: shift1 === false ? null : seniority1,
            seniority2: shift2 === false ? null : seniority2,
            seniority3: shift3 === false ? null : seniority3,
            seniority4: shift4 === false ? null : seniority4,
            shift1,
            shift2,
            shift3,
            shift4,
            qualifications: qualifications === '' ? null : qualifications,
            greenLine: greenLine === '' ? null : greenLine,
            postingStartDate: DateTime.fromFormat(
                postingStartDate,
                'ff'
            ).toISO(),
            endDate: DateTime.fromFormat(endDate, 'ff').toISO(),
            additionalDetails:
                additionalDetails === ''
                    ? null
                    : additionalDetails.slice(0, MAX_ADDITIONAL_DETAILS_LENGTH),
            biddingGroup,
            jobGrade,
            seniorityForJobBid,
            reqNumber: reqNumber === '' ? null : reqNumber,
        };
        if (!bidObj.id) {
            return createdBidObject;
        }
        return { id: bidObj.id, ...createdBidObject };
    };
    const cancelHandler = (event) => {
        event.preventDefault();
        clearForm();
    };
    return (
        <form
            style={{ display: 'flex', flexDirection: 'column' }}
            onSubmit={saveHandler}
        >
            <PromptValidState
                isFormChanged={isFormDirty}
                saveChanges={saveHandler}
                areAllFieldsValid={areAllFieldsValid}
            />
            {loading && (
                <CircularProgress
                    size="lg"
                    variant="indeterminate"
                    title={'Loading'}
                />
            )}
            {!loading && (
                <>
                    <Grid
                        container
                        spacing={4}
                        className={styles.formGridSection}
                    >
                        <Grid item sm={12}>
                            <Typography
                                variant="h4"
                                className={styles.formGridSectionHeader}
                            >
                                Facility Information
                            </Typography>
                        </Grid>
                        <Grid item sm={4}>
                            <SelectField
                                required={requiredFields.includes('facility')}
                                label={'Facility or Business Unit'}
                                value={facility ? facility.id.toString() : ''}
                                onChange={facilityChangeHandler}
                                error={
                                    (submitAttempted || facilityHasChanged) &&
                                    requiredFields.includes('facility') &&
                                    !facilityIsValid
                                }
                            >
                                <Option disabled hidden value="">
                                    Choose a facility/business unit
                                </Option>
                                {!error &&
                                    selectedAdminGroup.facilityOrBusinessUnits.flatMap(
                                        (facilityOption, index) =>
                                            facilityOption.id ? (
                                                <Option
                                                    value={facilityOption.id.toString()}
                                                    key={index}
                                                >
                                                    {facilityOption.displayName}
                                                </Option>
                                            ) : (
                                                []
                                            )
                                    )}
                            </SelectField>
                        </Grid>
                        <Grid item sm={4}>
                            <TextField
                                required={requiredFields.includes('department')}
                                label={'Department'}
                                value={department}
                                onChange={departmentChangeHandler}
                                error={
                                    (submitAttempted || departmentHasChanged) &&
                                    requiredFields.includes('department') &&
                                    !departmentIsValid
                                }
                                className={
                                    (submitAttempted || departmentHasChanged) &&
                                    requiredFields.includes('department') &&
                                    !departmentIsValid
                                        ? styles.formErrorBorder
                                        : {}
                                }
                            />
                        </Grid>
                        <Grid item sm={4}>
                            <TextField
                                required={requiredFields.includes(
                                    'currentWorkforce'
                                )}
                                size="small"
                                label={'Current Workforce'}
                                value={currentWorkforce}
                                onChange={currentWorkforceHandler}
                                helperText={'Enter numbers and commas only'}
                                error={
                                    (submitAttempted ||
                                        currentWorkforceHasChanged) &&
                                    requiredFields.includes(
                                        'currentWorkforce'
                                    ) &&
                                    !currentWorkforceIsValid
                                }
                                className={
                                    (submitAttempted ||
                                        currentWorkforceHasChanged) &&
                                    requiredFields.includes(
                                        'currentWorkforce'
                                    ) &&
                                    !currentWorkforceIsValid
                                        ? styles.formErrorBorder
                                        : {}
                                }
                            />
                        </Grid>
                        <Grid item sm={4}>
                            <SelectField
                                required={requiredFields.includes(
                                    'biddingGroup'
                                )}
                                label={'Personnel Sub Area For This Job Bid'}
                                value={
                                    biddingGroup
                                        ? biddingGroup.id.toString()
                                        : ''
                                }
                                onChange={biddingGroupHandler}
                                error={
                                    (submitAttempted ||
                                        biddingGroupHasChanged) &&
                                    requiredFields.includes('biddingGroup') &&
                                    !biddingGroupIsValid
                                }
                            >
                                <Option disabled hidden value="">
                                    Choose a personnel sub area
                                </Option>
                                {!error &&
                                    selectedAdminGroup.biddingGroups
                                        .filter((bg) => bg.hasCycleSettings)
                                        .map((group, index) => (
                                            <Option
                                                value={group.id.toString()}
                                                key={index}
                                            >
                                                {group.displayName}
                                            </Option>
                                        ))}
                            </SelectField>
                        </Grid>
                        <Grid item sm={4}>
                            <FormLabel
                                className={styles.formLabel}
                                required={requiredFields.includes(
                                    'postingStartDate'
                                )}
                                error={
                                    !postingStartDateIsValid &&
                                    requiredFields.includes(
                                        'postingStartDate'
                                    ) &&
                                    (fromActiveJobAndUnchanged ||
                                        submitAttempted)
                                }
                            >
                                Job Bid Posting Start Date
                            </FormLabel>
                            <DateInput
                                required={requiredFields.includes(
                                    'postingStartDate'
                                )}
                                disabled={!biddingGroupIsValid}
                                value={postingStartDate}
                                minDate={new Date()}
                                setValue={postingDateHandler}
                                helperText={
                                    'Choose date and time the job bid will go live.'
                                }
                                dayRestriction={
                                    biddingGroup
                                        ? biddingGroup.dayToGoActive
                                        : null
                                }
                                weekInMonthOptions={
                                    biddingGroup
                                        ? biddingGroup.bidCycleInterval
                                        : null
                                }
                                enableTime // TODO: find better way to handle time (not editable)
                                error={
                                    !postingStartDateIsValid &&
                                    requiredFields.includes(
                                        'postingStartDate'
                                    ) &&
                                    (fromActiveJobAndUnchanged ||
                                        submitAttempted)
                                }
                            />
                        </Grid>
                        <Grid item sm={4}>
                            <TextField
                                readOnly
                                label={'Job Bid End Date'}
                                value={endDate}
                            />
                        </Grid>
                    </Grid>

                    <Grid
                        container
                        spacing={4}
                        className={styles.formGridSection}
                    >
                        <Grid item sm={12}>
                            <Typography
                                variant="h4"
                                className={styles.formGridSectionHeader}
                            >
                                Job Information
                            </Typography>
                        </Grid>
                        <Grid item sm={4}>
                            <SelectField
                                required={requiredFields.includes(
                                    'classification'
                                )}
                                label={'Classification'}
                                value={
                                    classification
                                        ? classification.id.toString()
                                        : ''
                                }
                                onChange={classificationChangeHandler}
                                error={
                                    (submitAttempted ||
                                        classificationHasChanged) &&
                                    requiredFields.includes('classification') &&
                                    !classificationIsValid
                                }
                            >
                                <Option disabled hidden value="">
                                    Choose a classification
                                </Option>
                                {props.classificationList.map(
                                    (eachClassification, index) => (
                                        <Option
                                            key={index}
                                            value={eachClassification.id.toString()}
                                        >
                                            {eachClassification.classification}
                                        </Option>
                                    )
                                )}
                            </SelectField>
                        </Grid>
                        <Grid item sm={4}>
                            <SelectField
                                required={requiredFields.includes('jobGrade')}
                                readOnly={true}
                                label="Job Grade"
                                value={jobGrade}
                                onChange={jobGradeChangeHandler}
                                error={
                                    (submitAttempted || jobGradeHasChanged) &&
                                    requiredFields.includes('jobGrade') &&
                                    !JobGradeIsValid
                                }
                            >
                                <Option disabled hidden value="">
                                    Choose a job grade
                                </Option>
                                <Option value="0">0</Option>
                                <Option value="1">1</Option>
                                <Option value="2">2</Option>
                                <Option value="3">3</Option>
                                <Option value="4">4</Option>
                                <Option value="5">5</Option>
                                <Option value="6">6</Option>
                                <Option value="7">7</Option>
                                <Option value="8">8</Option>
                                <Option value="9">9</Option>
                                <Option value="10">10</Option>
                            </SelectField>
                        </Grid>
                        <Grid item sm={4}>
                            <TextField
                                size="small"
                                label={'Supervisor(s)'}
                                value={supervisors}
                                onChange={supervisorsChangeHandler}
                            />
                        </Grid>
                        <Grid item sm={4}>
                            <TextField
                                size="small"
                                label="OT%"
                                value={otPerc}
                                onChange={otPercChangeHandler}
                                required={requiredFields.includes('otPerc')}
                                helperText="Enter a percentage up to 100%"
                                error={
                                    (submitAttempted || otPercHasChanged) &&
                                    requiredFields.includes('otPerc') &&
                                    !otPercIsValid
                                }
                            />
                        </Grid>
                        <Grid item sm={4}>
                            <SelectField
                                required={requiredFields.includes('cipp')}
                                label="CIPP"
                                value={cipp}
                                onChange={cippChangeHandler}
                                error={
                                    (submitAttempted || cippHasChanged) &&
                                    requiredFields.includes('cipp') &&
                                    !cippIsValid
                                }
                            >
                                <Option disabled hidden value="">
                                    Choose yes or no
                                </Option>
                                <Option value="Y">Yes</Option>
                                <Option value="N">No</Option>
                            </SelectField>
                        </Grid>
                        <Grid item sm={4}>
                            {cipp === 'Y' && (
                                <TextField
                                    type="number"
                                    size="small"
                                    label="CIPP%"
                                    value={cippPerc}
                                    onChange={cippPercHandler}
                                    required={requiredFields.includes(
                                        'cippPerc'
                                    )}
                                    helperText="Enter the percentage of CIPP award upto 500%"
                                    error={
                                        !cippPercIsValid &&
                                        requiredFields.includes('cippPerc') &&
                                        (cippPercHasChanged || submitAttempted)
                                    }
                                    endAdornment={
                                        <InputAdornment position="end">
                                            %
                                        </InputAdornment>
                                    }
                                    inputProps={{ max: 500, min: 0 }}
                                />
                            )}
                        </Grid>
                        <Grid item sm={4}>
                            <TextField
                                size="small"
                                type="number"
                                label="Number of Openings"
                                value={noOfOpenings}
                                onChange={noOfOpeningsHandler}
                                required={requiredFields.includes(
                                    'noOfOpenings'
                                )}
                                error={
                                    !noOfOpeningsIsValid &&
                                    requiredFields.includes('noOfOpenings') &&
                                    (noOfOpeningsHasChanged ||
                                        submitAttempted ||
                                        fromActiveJobAndUnchanged)
                                }
                                helperText="Enter a number up to 999."
                            />
                        </Grid>
                        <Grid item sm={4}>
                            <div style={{ textAlign: 'left' }}>
                                <FormControl component="fieldset">
                                    <FormLabel
                                        id="MARKER"
                                        className={styles.formLabel}
                                        required={requiredFields.includes(
                                            'shifts'
                                        )}
                                        component="legend"
                                        error={
                                            (submitAttempted ||
                                                shiftHasChanged) &&
                                            requiredFields.includes('shifts') &&
                                            !shiftsAreValid
                                        }
                                    >
                                        Shifts Department Runs
                                    </FormLabel>
                                    <FormGroup>
                                        <div>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        name="shift1"
                                                        checked={shift1}
                                                        onChange={shift1Handler}
                                                    />
                                                }
                                                label="1st Shift"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        name="shift2"
                                                        checked={shift2}
                                                        onChange={shift2Handler}
                                                    />
                                                }
                                                label="2nd Shift"
                                            />
                                        </div>
                                    </FormGroup>
                                    <FormGroup>
                                        <div>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        name="shift3"
                                                        checked={shift3}
                                                        onChange={shift3Handler}
                                                    />
                                                }
                                                label="3rd Shift"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        name="shift4"
                                                        checked={shift4}
                                                        onChange={shift4Handler}
                                                    />
                                                }
                                                label="4th Shift"
                                            />
                                        </div>
                                    </FormGroup>
                                </FormControl>
                            </div>
                        </Grid>
                        <Grid item sm={4}>
                            <SelectField
                                required={requiredFields.includes(
                                    'seniorityForJobBid'
                                )}
                                label="Seniority For This Job Bid"
                                value={seniorityForJobBid}
                                onChange={seniorityForJobBidHandler}
                                helperText="Choose seniority type most considered for this job"
                                error={
                                    (submitAttempted || seniorityHasChanged) &&
                                    requiredFields.includes(
                                        'seniorityForJobBid'
                                    ) &&
                                    !seniorityForJobBidIsValid
                                }
                            >
                                <Option disabled hidden value="">
                                    Classification Seniority
                                </Option>
                                <Option value="plant-wide">Plant-wide</Option>
                                <Option value="classification">
                                    Classification
                                </Option>
                                <Option value="department">Department</Option>
                            </SelectField>
                        </Grid>
                        <Grid item sm={8}>
                            <TextField
                                label="Additional Details"
                                type="text"
                                multiline
                                minRows={3}
                                maxRows={8}
                                className={styles.additionalDetailsField}
                                helperText={`${additionalDetails.length}/${MAX_ADDITIONAL_DETAILS_LENGTH} characters`}
                                inputProps={{
                                    maxLength: MAX_ADDITIONAL_DETAILS_LENGTH,
                                }}
                                value={additionalDetails}
                                onChange={additionalDetailsHandler}
                            />
                        </Grid>
                    </Grid>

                    <Grid
                        container
                        spacing={4}
                        className={styles.formGridSection}
                    >
                        <Grid item sm={12}>
                            <Typography
                                variant="h4"
                                className={styles.formGridSectionHeader}
                            >
                                Additional Information
                            </Typography>
                        </Grid>
                        {shift1 && (
                            <Grid item sm={4}>
                                <FormLabel
                                    className={styles.formLabel}
                                    required={requiredFields.includes(
                                        'seniority1'
                                    )}
                                    error={
                                        submitAttempted &&
                                        requiredFields.includes('seniority1') &&
                                        !seniority1IsValid
                                    }
                                >
                                    Junior Permanent Seniority on 1st
                                </FormLabel>
                                {shift1 && (
                                    <DateInput
                                        value={seniority1}
                                        setValue={seniority1Handler}
                                        enableTime={false}
                                        maxDate={new Date()}
                                        error={
                                            submitAttempted &&
                                            requiredFields.includes(
                                                'seniority1'
                                            ) &&
                                            !seniority1IsValid
                                        }
                                    />
                                )}
                            </Grid>
                        )}
                        {shift2 && (
                            <Grid item sm={4}>
                                <FormLabel
                                    className={styles.formLabel}
                                    required={requiredFields.includes(
                                        'seniority2'
                                    )}
                                    error={
                                        submitAttempted &&
                                        requiredFields.includes('seniority2') &&
                                        !seniority2IsValid
                                    }
                                >
                                    Junior Permanent Seniority on 2nd
                                </FormLabel>
                                {shift2 && (
                                    <DateInput
                                        value={seniority2}
                                        setValue={seniority2Handler}
                                        maxDate={new Date()}
                                        enableTime={false}
                                        error={
                                            submitAttempted &&
                                            requiredFields.includes(
                                                'seniority2'
                                            ) &&
                                            !seniority2IsValid
                                        }
                                    />
                                )}
                            </Grid>
                        )}
                        {shift3 && (
                            <Grid item sm={4}>
                                <FormLabel
                                    className={styles.formLabel}
                                    required={requiredFields.includes(
                                        'seniority3'
                                    )}
                                    error={
                                        submitAttempted &&
                                        requiredFields.includes('seniority3') &&
                                        !seniority3IsValid
                                    }
                                >
                                    Junior Permanent Seniority on 3rd
                                </FormLabel>
                                {shift3 && (
                                    <DateInput
                                        value={seniority3}
                                        setValue={seniority3Handler}
                                        maxDate={new Date()}
                                        enableTime={false}
                                        error={
                                            submitAttempted &&
                                            requiredFields.includes(
                                                'seniority3'
                                            ) &&
                                            !seniority3IsValid
                                        }
                                    />
                                )}
                            </Grid>
                        )}
                        {shift4 && (
                            <Grid item sm={4}>
                                <FormLabel
                                    className={styles.formLabel}
                                    required={requiredFields.includes(
                                        'seniority4'
                                    )}
                                    error={
                                        submitAttempted &&
                                        requiredFields.includes('seniority4') &&
                                        !seniority4IsValid
                                    }
                                >
                                    Junior Permanent Seniority on 4th
                                </FormLabel>
                                {shift4 && (
                                    <DateInput
                                        value={seniority4}
                                        setValue={seniority4Handler}
                                        maxDate={new Date()}
                                        enableTime={false}
                                        error={
                                            submitAttempted &&
                                            requiredFields.includes(
                                                'seniority4'
                                            ) &&
                                            !seniority4IsValid
                                        }
                                    />
                                )}
                            </Grid>
                        )}
                        <Grid item sm={4}>
                            <TextField
                                size="small"
                                label="Qualifications"
                                value={qualifications}
                                onChange={qualificationsHandler}
                                helperText="List all qualifications needed for this job opening."
                            />
                        </Grid>
                        <Grid item sm={4}>
                            <TextField
                                size="small"
                                label="Green-Line"
                                onChange={greenLineHandler}
                                value={greenLine}
                            />
                        </Grid>
                        <Grid item sm={4}>
                            <TextField
                                size="small"
                                label="Req Number"
                                value={reqNumber}
                                onChange={reqNumberChangeHandler}
                            />
                        </Grid>
                    </Grid>
                    <Box
                        mt={2}
                        justifyContent="space-between"
                        justifyItems="flex-end"
                        display="flex"
                    >
                        <Box>{props.deleteButt}</Box>
                        <Box>
                            <Button
                                variant="secondary"
                                onClick={cancelHandler}
                                style={{ marginRight: '0.5rem' }}
                            >
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={saveHandler}>
                                Submit
                            </Button>
                        </Box>
                    </Box>
                </>
            )}
        </form>
    );
};

BidForm.propTypes = {
    bidObj: PropTypes.object,
    updateBid: PropTypes.func.isRequired,
    setIsValid: PropTypes.func,
    activeCopyCallCr: PropTypes.bool,
    deleteButt: PropTypes.element,
    classificationList: PropTypes.array.isRequired,
};
